import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { BrowserProvider, JsonRpcProvider, Contract, formatEther, parseEther } from 'ethers';
import DbankABI from './contracts/Dbank.json'; // Verified ABI
import { AuthContext } from '../AuthContext';
import debounce from 'lodash/debounce'; // Import debounce from Lodash

const DBank = () => {
  const { isAuthenticated, ensName, loginWithSIWE, walletAddress: contextWalletAddress } = useContext(AuthContext) || {};
  const [localWalletAddress, setLocalWalletAddress] = useState(contextWalletAddress); // Local state for wallet address
  const contractAddress = "0xbdcaa45e3c8acd850d522aaeaa73125447c9983f"; // Sepolia
  const [contract, setContract] = useState(null);
  const [balance, setBalance] = useState('0');
  const [dividendEntitlement, setDividendEntitlement] = useState('0');
  const [lastDividendDate, setLastDividendDate] = useState('N/A');
  const [buyAmount, setBuyAmount] = useState('5'); // Default to 5 tokens
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasInitialized = useRef(false); // Track initialization
  const intervalRef = useRef(null); // Reference to interval
  const INFURA_URL = "https://sepolia.infura.io/v3/674854f82eaa44c994c2eb347b01b279"; // Your Infura key

  const updateState = async (contract) => {
    if (!contract || !localWalletAddress) {
      console.log("UpdateState skipped: contract or localWalletAddress missing");
      return;
    }
    try {
      console.log("Fetching balanceOf for:", localWalletAddress);
      let bal;
      try { bal = await contract.balanceOf(localWalletAddress); } catch (balErr) {
        console.error("balanceOf failed:", balErr);
        bal = 0n;
      }
      console.log("Raw balanceOf result:", bal.toString());
      const balanceValue = bal.toString() === '0' || bal.toString() === '' ? '0' : formatEther(bal);
      console.log("Fetching getDividendEntitlement for:", localWalletAddress);
      let entitlement;
      try { entitlement = await contract.getDividendEntitlement(localWalletAddress); } catch (entErr) {
        console.error("getDividendEntitlement failed:", entErr);
        entitlement = 0n;
      }
      console.log("Raw entitlement result:", entitlement.toString());
      const entitlementValue = entitlement.toString() === '0' || entitlement.toString() === '' ? '0' : formatEther(entitlement);
      console.log("Fetching lastDividendDate");
      let lastDate;
      try { lastDate = await contract.lastDividendDate(); } catch (dateErr) {
        console.error("lastDividendDate failed:", dateErr);
        lastDate = 0n;
      }
      console.log("Raw lastDividendDate result:", lastDate.toString());
      const lastDateValue = lastDate.toString() === '0' ? 'N/A' : new Date(Number(lastDate) * 1000).toLocaleDateString();
      setBalance(balanceValue);
      setDividendEntitlement(entitlementValue);
      setLastDividendDate(lastDateValue);
      setLoading(false);
      setError(null);
    } catch (err) {
      console.error("Unexpected updateState error:", err);
      setError(`Unexpected error during state fetch. Error: ${err.message}`);
      setLoading(false);
    }
  };

  const getProvider = useCallback(async () => {
    try {
      if (!window.ethereum) throw new Error("MetaMask not detected. Please install or enable it.");
      const provider = new BrowserProvider(window.ethereum); // Primary MetaMask provider
      const network = await provider.getNetwork();
      if (network.chainId !== 11155111n) {
        await provider.send("wallet_switchEthereumChain", [{ chainId: "0xAA36A7" }]);
        await new Promise(resolve => setTimeout(resolve, 4000)); // Match Web3Identity.js delay
      }
      return provider;
    } catch (err) {
      console.warn("MetaMask failed, falling back to Infura:", err.message);
      return new JsonRpcProvider(INFURA_URL); // Fallback to Infura
    }
  }, []);

  const initContract = useCallback(async () => {
    if (!localWalletAddress || !isAuthenticated || hasInitialized.current) return;
    try {
      console.log("Initializing contract for wallet:", localWalletAddress, "isAuthenticated:", isAuthenticated);
      const provider = await getProvider();
      const updatedNetwork = await provider.getNetwork();
      console.log("Connected network after adjustment:", updatedNetwork.name, "chainId:", updatedNetwork.chainId);
      const signer = await provider.getSigner();
      console.log("Signer address:", await signer.getAddress());
      const contractInstance = new Contract(contractAddress, DbankABI, signer);
      setContract(contractInstance);
      await updateState(contractInstance);
      hasInitialized.current = true; // Mark as initialized
    } catch (err) {
      console.error("Contract initialization failed:", err);
      setError(`Failed to load contract. Error: ${err.message}. Ensure MetaMask or Infura is accessible on Sepolia (chainId 11155111).`);
      setLoading(false);
    }
  }, [localWalletAddress, isAuthenticated, getProvider]);

  useEffect(() => {
    // Sync localWalletAddress with contextWalletAddress
    if (contextWalletAddress && contextWalletAddress !== localWalletAddress) {
      console.log("Syncing wallet address:", contextWalletAddress);
      setLocalWalletAddress(contextWalletAddress);
    }
    initContract();

    // Set up interval with ref to clear it
    if (!intervalRef.current && !localWalletAddress && isAuthenticated && !hasInitialized.current) {
      intervalRef.current = setInterval(() => {
        console.log("Retrying wallet address fetch due to undefined...");
        const fetchAddress = async () => {
          try {
            const provider = await getProvider();
            const signer = await provider.getSigner();
            const addr = await signer.getAddress();
            if (addr && addr !== localWalletAddress) {
              console.log("Fetched address:", addr);
              setLocalWalletAddress(addr); // Update local state
            }
          } catch (err) {
            console.error("Address fetch failed:", err);
          }
        };
        fetchAddress();
      }, 2000); // Check every 2 seconds
    }

    // Clear interval if conditions are met
    if (localWalletAddress || hasInitialized.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [initContract, contextWalletAddress, localWalletAddress, isAuthenticated]);

  // Debounced buyTokens function
  const debouncedBuyTokens = useCallback(
    debounce(async () => {
      if (!contract || !isAuthenticated || !buyAmount || parseInt(buyAmount) <= 0) {
        console.log("BuyTokens failed: contract:", contract, "isAuthenticated:", isAuthenticated, "walletAddress:", localWalletAddress, "buyAmount:", buyAmount);
        return;
      }
      try {
        console.log("Attempting to buy", buyAmount, "tokens for wallet:", localWalletAddress);
        const amount = parseInt(buyAmount); // Convert to number
        if (isNaN(amount)) throw new Error("Invalid buy amount");
        const pricePerTokenInWei = parseEther("0.001"); // Price per token in wei
        const totalValueInWei = BigInt(amount) * pricePerTokenInWei; // Total value in wei
        if (totalValueInWei <= 0n) throw new Error("Calculated value must be greater than zero");
        console.log("Calculated value:", formatEther(totalValueInWei), "ETH");
        const tx = await contract.buyTokens({ value: totalValueInWei, gasLimit: 200000 });
        console.log("Transaction sent, hash:", tx.hash);
        await tx.wait();
        console.log("Transaction confirmed, hash:", tx.hash);
        await updateState(contract);
        alert(`Purchase successful! Bought ${buyAmount} $DBANK. Tx Hash: ${tx.hash}`);
      } catch (err) {
        console.error("BuyTokens error:", err);
        alert("Purchase failed: " + (err.message || "Unknown error. Check MetaMask for details."));
      }
    }, 300), // 300ms debounce delay
    [contract, isAuthenticated, buyAmount, localWalletAddress, updateState] // Dependencies
  );

  const claimDividends = async () => {
    if (!contract || !isAuthenticated) {
      console.log("ClaimDividends failed: contract:", contract, "isAuthenticated:", isAuthenticated, "walletAddress:", localWalletAddress);
      return;
    }
    try {
      console.log("Attempting to claim dividends for wallet:", localWalletAddress);
      const tx = await contract.claimDividends({ gasLimit: 100000 });
      console.log("Transaction sent, hash:", tx.hash);
      await tx.wait();
      console.log("Transaction confirmed, hash:", tx.hash);
      await updateState(contract);
      alert(`Dividends claimed successfully! Tx Hash: ${tx.hash}`);
    } catch (err) {
      console.error("ClaimDividends error:", err);
      alert("Claim failed: " + err.message);
    }
  };

  return (
    <section className="dbank-container">
      <Helmet>
        <title>$DBANK Shares - Free for All Ownership</title>
        <meta property="og:title" content="$DBANK Shares - Coming Soon" />
        <meta property="og:description" content="$DBANK Shares, a new IPO launching on BankSystem.eth, feature bank ownership with 1B total shares: 100M for sale at an offering price of $0.45 per share, and 900M founder-held forever." />
        <meta property="og:image" content="https://banksystem.co/dbanks-preview.jpg" />
        <meta property="og:url" content="https://banksystem.co/dbanks" />
        <meta property="og:type" content="website" />
      </Helmet>
      <div
        style={{
          background: 'linear-gradient(135deg, #1a1a1a, #000)', // Gold Card gradient
          padding: '20px',
          borderRadius: '10px',
          boxShadow: '0 0 20px gold, inset 0 0 10px rgba(255, 215, 0, 0.3)', // Gold depth
          transition: 'box-shadow 0.3s ease',
          maxWidth: '600px',
          margin: '0 auto',
          position: 'relative',
          border: '2px solid gold',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.boxShadow = '0 0 40px gold, inset 0 0 15px rgba(255, 215, 0, 0.5)')}
        onMouseLeave={(e) => (e.currentTarget.style.boxShadow = '0 0 20px gold, inset 0 0 10px rgba(255, 215, 0, 0.3)')}
      >
        {/* Ornate Border Effect */}
        <div
          style={{
            position: 'absolute',
            top: '-5px', left: '-5px', right: '-5px', bottom: '-5px',
            border: '2px dashed rgba(255, 215, 0, 0.7)',
            borderRadius: '12px',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            background: 'linear-gradient(135deg, #f5f5dc, #fff8e7)', // Parchment effect
            padding: '20px',
            borderRadius: '8px',
            border: '1px solid #d2b48c',
            boxShadow: 'inset 0 0 10px rgba(0, 0, 0, 0.2)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Decorative Lines */}
          <div style={{ position: 'absolute', top: '10px', left: '10px', right: '10px', borderTop: '1px dashed #8b4513', zIndex: 1 }} />
          <div style={{ position: 'absolute', bottom: '10px', left: '10px', right: '10px', borderBottom: '1px dashed #8b4513', zIndex: 1 }} />

          <h1 style={{ textAlign: 'center', color: '#880808', fontFamily: 'Georgia, serif', fontSize: '28px', marginBottom: '10px' }}>Certificate of Ownership</h1>
          <p style={{ textAlign: 'center', color: '#000', fontFamily: 'Georgia, serif', fontSize: '16px', marginBottom: '20px' }}>
            This certifies that the shareholder below owns $DBANK Shares in BankSystem.eth.
          </p>

          {/* Shareholder Details */}
          <div style={{ color: '#000', fontFamily: 'Arial, sans-serif', fontSize: '14px', marginBottom: '10px' }}>
            <strong>Shareholder:</strong> {ensName || localWalletAddress?.substring(0, 6) + '...' || 'Unknown'}
            <br />
            <strong>Shares Owned:</strong> {balance} $DBANK
            <br />
            <strong>Certificate #:</strong> {localWalletAddress?.substring(2, 10) || 'N/A'} {/* Unique ID from address */}
            <br />
            <strong>Issue Date:</strong> {new Date().toLocaleDateString()}
          </div>

          {/* Dividend Section */}
          <div style={{ margin: '20px 0', color: '#000', fontFamily: 'Arial, sans-serif', fontSize: '14px' }}>
            <strong>Total Shares:</strong> 1,000,000,000 $DBANK
            <br />
            <strong>For Sale:</strong> 100,000,000 $DBANK
            <br />
            <strong>Dividend Entitlement:</strong> {dividendEntitlement} ETH
            <br />
            <strong>Last Paid:</strong> {lastDividendDate}
          </div>

          {/* Connection Status */}
          <div style={{ backgroundColor: '#333', padding: '1rem', borderRadius: '5px', margin: '20px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${isAuthenticated ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span style={{ color: isAuthenticated ? '#00FF00' : '#FF0000', fontFamily: 'Arial, sans-serif', fontSize: '14px' }}>
                {isAuthenticated ? `✓ Wallet Connected: ${ensName || localWalletAddress?.substring(0, 6) + '...' || 'Unknown'}` : 'Not Connected'}
              </span>
            </div>
            {!isAuthenticated && (
              <button
                onClick={loginWithSIWE}
                style={{
                  background: 'transparent',
                  border: '2px solid gold',
                  color: 'gold',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontFamily: 'Arial, sans-serif',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'gold'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              >
                Connect Wallet
              </button>
            )}
          </div>

          {error && <p style={{ textAlign: 'center', color: 'red', fontFamily: 'Arial, sans-serif', fontSize: '14px' }}>{error}</p>}
          {loading ? (
            <p style={{ textAlign: 'center', color: '#000', fontFamily: 'Arial, sans-serif', fontSize: '14px' }}>Loading...</p>
          ) : (
            <>
              <div style={{ margin: '20px 0', textAlign: 'center' }}>
                <input
                  type="number"
                  value={buyAmount}
                  onChange={(e) => setBuyAmount(e.target.value)}
                  placeholder="Enter amount to buy"
                  style={{
                    background: '#f5f5dc',
                    color: '#000',
                    border: '1px solid #d2b48c',
                    padding: '8px',
                    marginRight: '10px',
                    width: '100px',
                    fontFamily: 'Arial, sans-serif',
                    fontSize: '14px',
                  }}
                />
                <button onClick={debouncedBuyTokens} className="btn btn-dark-red" style={{ borderColor: 'black', minWidth: '140px', height: '40px', padding: '0 18px' }}>
                  Buy {buyAmount} $DBANK (0.001 ETH each)
                </button>
              </div>
              <div style={{ margin: '20px 0', textAlign: 'center' }}>
                <button onClick={claimDividends} className="btn btn-dark-red" style={{ borderColor: 'black', minWidth: '140px', height: '40px', padding: '0 18px' }}>
                  Claim Dividends
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default DBank;
